import { Checkbox, Form, Input, Select } from 'antd'
import clsx from 'clsx'
import { type FC, type KeyboardEvent, useCallback, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { FaCaretRight } from 'react-icons/fa'

import {
  Button,
  BasicTr as Tr,
  NormalizedInputNumber
} from '@/components/elements'
import { ACTIONS, LABELS } from '@/constants/common/message.constants'
import { AdditionalPaymentCategoriesSelect } from '@/features/additional-payment-categories/components'
import { useAdditionalPaymentCategoriesSelectQuery } from '@/features/additional-payment-categories/hooks'
import { TaxCategorySelect } from '@/features/shared/components'
import { REPORT_TYPE, ROUND_TYPE_OPTIONS } from '@/libs/api/types'

import {
  PRICING_MODE,
  type ShipmentOrderAdditionalFormValue
} from '../../models/shipment-order-additional-form-value'

import { type FieldArrayName } from './field-array-name'

type Props = {
  /** RHF `useFieldArray` の name (請求側 / 支払側どちらか) */
  fieldArrayName: FieldArrayName
  /** RHF が割り当てた行キー (`useFieldArray({ keyName: 'rhfId' })`) */
  rhfId: string
  /** field index */
  index: number
  /** テナント key (Select で使う) */
  tenantKey: string
  /** 編集可否 */
  isEditing: boolean
  /** テーブル総列数 (展開行の colSpan 用。Section の colgroup と一致させる) */
  columnCount: number
  /** 行削除 */
  onRemove: (index: number) => void
}

// AntD Form.Item が複数 Controller を含むのを避けるため、フィールド単位で Form.Item をネスト
/**
 * 配送依頼 (ShipmentOrder) 付帯費用 1 行分の表示/編集 UI (行単位アコーディオン)。
 *
 * 主行の列構成:
 *   [開閉トグル] [カテゴリ] [運賃込み] [金額入力 (税抜)] [備考] [削除]
 *
 * #3938 派生のレイアウト崩れ対策: 変更頻度の低い 税区分・内税・切り上げ切り捨て
 * (端数処理) は主行に横並びで置かず、開閉トグルで主行直下に「縦展開」する。
 * 展開しても横幅を増やさないので狭いモーダルでも崩れない
 * (備考は常時表示、撰さん・小作さん合意)。運賃込み (INCLUDED) 行はこの 3 項目が非該当のため
 * トグル自体を出さない (既存の「INCLUDED 時は税区分・内税を隠す」挙動を踏襲)。
 *
 * shimizu-pg APPROVED 指摘 1 を反映:
 *   - `pricingMode='INCLUDED'`: 「運賃込み」チェックボックス ON、金額入力欄を非表示
 *   - `pricingMode='AMOUNT'`: チェックボックス OFF、金額入力欄が有効
 *
 * `pricingMode` の表示状態は `useWatch` で現在値を購読し、`setValue` 更新を即時反映する。
 */
export const ShipmentOrderAdditionalRow: FC<Props> = ({
  fieldArrayName,
  index,
  tenantKey,
  isEditing,
  columnCount,
  onRemove
}) => {
  const { control, setValue, getValues } =
    useFormContext<Record<string, ShipmentOrderAdditionalFormValue[]>>()

  // 税区分・内税・切り上げ切り捨ての展開状態 (行単位アコーディオン)。
  // 既定は閉じて省スペース、必要時にトグルで主行直下へ縦展開する。
  const [expanded, setExpanded] = useState(false)
  const toggleExpanded = useCallback(() => setExpanded((prev) => !prev), [])

  // VOC_PJ-867 #3821: カテゴリ選択時にマスタの既定税区分 (defaultTaxCategoryId) を
  // 税区分へ pre-fill するため取得（新規作成時と同じ状態に揃える）。
  const { data: additionalPaymentCategories } =
    useAdditionalPaymentCategoriesSelectQuery(tenantKey, REPORT_TYPE.DAILY)

  const handleRemove = useCallback(() => onRemove(index), [index, onRemove])

  // Enter キーによる form submit を防止
  const preventEnterSubmit = useCallback((e: KeyboardEvent) => {
    if (e.nativeEvent.isComposing) return
    if (e.key === 'Enter') e.preventDefault()
  }, [])

  const rowKeyPrefix = `${fieldArrayName}.${index}` as const

  // 内税フラグの永続化は請求側 (additionalCharges = ShipmentOrderAdditionalCharge) /
  // 支払側 (additionalPayments = DispatchDetailAdditionalPayment) ともに BE 対応済み (slice #3875)。
  // 両側とも操作・pre-fill 可能。

  // 現在の pricingMode を購読 (setValue 経由の更新を即時反映)
  const pricingMode = useWatch({
    control,
    name: `${rowKeyPrefix}.pricingMode`
  }) as ShipmentOrderAdditionalFormValue['pricingMode'] | undefined

  // 金額入力欄は `pricingMode='AMOUNT'` のときだけ有効。
  const isAmountDisabled = pricingMode !== PRICING_MODE.AMOUNT

  // 運賃込み (INCLUDED) のときは金額・税区分・内税・端数処理が不要。金額は非表示にして
  // 備考を colSpan で広げ、詳細 (税区分等) の開閉トグルも出さない。
  const isIncluded = pricingMode === PRICING_MODE.INCLUDED

  // 詳細 (税区分・内税・端数処理) を展開できるのは AMOUNT 行のみ。
  const canExpandDetail = !isIncluded
  const isDetailOpen = canExpandDetail && expanded

  return (
    <>
      <Tr
        // 詳細展開中は主行↔詳細行の境界線を消して一塊に見せる。
        className={isDetailOpen ? 'border-b-0' : undefined}
        data-testid={`shipment-order-additional-${fieldArrayName}-${index}`}
      >
        {/* 税区分・内税・切り上げ切り捨ての開閉トグル (左端・AMOUNT 行のみ)。
            閉=右向きキャレット / 開=90°回転で下向き (transition でなめらかに)。
            INCLUDED 行は 3 項目が非該当なのでセルのみ (空) にする。 */}
        <td className="p-1 text-center">
          {canExpandDetail && (
            <Button
              type="text"
              size="small"
              shape="circle"
              onClick={toggleExpanded}
              aria-expanded={expanded}
              aria-label={ACTIONS.SHOW_DETAIL}
              data-testid={`${fieldArrayName}-${index}-toggle-detail`}
              icon={
                <FaCaretRight
                  className={clsx([
                    'text-secondary transition-transform duration-200',
                    expanded && 'rotate-90'
                  ])}
                />
              }
            />
          )}
        </td>
        {/* カテゴリ */}
        <th className="p-2 text-left font-bold text-secondary">
          <Controller
            control={control}
            name={`${rowKeyPrefix}.additionalPaymentCategoryId`}
            render={({ field: rhfField, fieldState: { error } }) => (
              <Form.Item
                validateStatus={error?.message ? 'error' : undefined}
                help={error?.message}
                className="mb-0"
              >
                <AdditionalPaymentCategoriesSelect
                  tenantKey={tenantKey}
                  reportType={REPORT_TYPE.DAILY}
                  ref={rhfField.ref}
                  value={rhfField.value ?? undefined}
                  onBlur={rhfField.onBlur}
                  onChange={(v) => {
                    const next = (v ?? null) as number | null
                    rhfField.onChange(next)
                    if (next == null) return
                    // VOC_PJ-867 #3821: マスタの既定税区分を税区分へ pre-fill
                    // (案件個別 override 前の初期値・新規作成時と同じ挙動)。
                    const picked = additionalPaymentCategories?.find(
                      (a) => a.id === next
                    )
                    if (picked) {
                      setValue(
                        `${rowKeyPrefix}.taxCategoryId`,
                        picked.taxCategoryId
                      )
                      // VOC_PJ-844 #3624: APC の内税既定を pre-fill
                      // (APC.isTaxInclusive は必須なので常に値あり。ユーザは手動で切替可能)。
                      setValue(
                        `${rowKeyPrefix}.isTaxInclusive`,
                        picked.isTaxInclusive
                      )
                      // VOC_PJ-888 #3917: APC の端数処理 (roundType) も pre-fill。
                      // 税区分 (#3821) と対称化。手動で切上/切捨/四捨五入を上書き可能。
                      setValue(`${rowKeyPrefix}.roundType`, picked.roundType)
                      // VOC_PJ-865 #3819: 手動追加行はカテゴリ後決めのため、選択カテゴリの
                      // isFareIncludedByDefault を見て運賃込み (pricingMode) を初期化する。
                      // INCLUDED のときは amount も null に倒す。pricingMode は Controller
                      // 登録済みのため setValue で dirtyFields にも反映される。
                      const nextPricingMode = picked.isFareIncludedByDefault
                        ? PRICING_MODE.INCLUDED
                        : PRICING_MODE.AMOUNT
                      setValue(`${rowKeyPrefix}.pricingMode`, nextPricingMode, {
                        shouldDirty: true,
                        shouldValidate: true
                      })
                      if (nextPricingMode !== PRICING_MODE.AMOUNT) {
                        setValue(`${rowKeyPrefix}.amount`, null, {
                          shouldDirty: true,
                          shouldValidate: true
                        })
                      }
                      // VOC_PJ-1000 #5264: マスタの備考を依頼側の備考へ写す。
                      //
                      // 他の pre-fill（税区分・内税・端数処理・運賃込み）は構造化
                      // された既定値なので無条件に上書きするが、**備考は人が書いた
                      // 文章なので空のときだけ写す**。地点選択時に Site.note を写す
                      // 規則（shared/utils/site-note.util.ts の resolveSiteNote）と
                      // 同じ扱いで、空判定も同じく trim 後で見る。
                      const currentNote = getValues(`${rowKeyPrefix}.note`)
                      const isNoteEmpty =
                        currentNote == null || currentNote.trim() === ''
                      if (isNoteEmpty && picked.note) {
                        setValue(`${rowKeyPrefix}.note`, picked.note, {
                          shouldDirty: true,
                          shouldValidate: true
                        })
                      }
                    }
                  }}
                  disabled={!isEditing}
                />
              </Form.Item>
            )}
          />
        </th>
        {/* 「運賃込み」 (INCLUDED) チェックボックス。金額側へ寄せて間隔を詰める (text-right) */}
        {/*
          pricingMode は Controller 経由で更新する。プレーン setValue だと未登録フィールド扱いとなり
          shouldDirty が dirtyFields に反映されず、INCLUDED→AMOUNT の切替で保存ボタンが活性しない
          (dirtyFields ベースの保存条件を持つ編集モーダルで顕在化)。Controller の field.onChange で
          登録済みフィールドとして dirty を確実に立てる。
        */}
        <td className="px-1 py-2 text-right">
          <Controller
            control={control}
            name={`${rowKeyPrefix}.pricingMode`}
            render={({ field: rhfField }) => (
              <Checkbox
                checked={rhfField.value === PRICING_MODE.INCLUDED}
                onChange={(e) => {
                  const next = e.target.checked
                    ? PRICING_MODE.INCLUDED
                    : PRICING_MODE.AMOUNT
                  rhfField.onChange(next)
                  // INCLUDED へ切り替えるときは金額を null に倒す
                  if (next !== PRICING_MODE.AMOUNT) {
                    setValue(`${rowKeyPrefix}.amount`, null, {
                      shouldDirty: true,
                      shouldValidate: true
                    })
                  }
                }}
                disabled={!isEditing}
                data-testid={`${fieldArrayName}-${index}-included-checkbox`}
              >
                {LABELS.INCLUDED_IN_FARE}
              </Checkbox>
            )}
          />
        </td>
        {/* 金額 (税抜)。INCLUDED 時は非表示にし、備考を colSpan で広げる。
            左 padding を詰めて「運賃込み」に寄せる (ps-0)。 */}
        {!isIncluded && (
          <td className="py-2 pe-2 ps-0 text-left">
            <Controller
              control={control}
              name={`${rowKeyPrefix}.amount`}
              render={({ field: rhfField, fieldState: { error } }) => (
                <Form.Item
                  validateStatus={error?.message ? 'error' : undefined}
                  help={error?.message}
                  className="mb-0"
                >
                  <NormalizedInputNumber
                    prefix="¥"
                    placeholder={LABELS.AMOUNT_YEN}
                    min={null}
                    value={rhfField.value ?? null}
                    onChange={(v) => {
                      if (v == null || Number.isNaN(v)) {
                        rhfField.onChange(null)
                      } else {
                        rhfField.onChange(v)
                      }
                    }}
                    onBlur={rhfField.onBlur}
                    onKeyDown={preventEnterSubmit}
                    id={rhfField.name}
                    disabled={!isEditing || isAmountDisabled}
                  />
                </Form.Item>
              )}
            />
          </td>
        )}
        {/* 備考 (常時表示)。INCLUDED 時は金額ぶん広げる (colSpan) */}
        <td className="p-2 text-left" colSpan={isIncluded ? 2 : undefined}>
          <Controller
            control={control}
            name={`${rowKeyPrefix}.note`}
            render={({ field: rhfField, fieldState: { error } }) => (
              <Form.Item
                validateStatus={error?.message ? 'error' : undefined}
                help={error?.message}
                className="mb-0"
              >
                <Input
                  value={rhfField.value ?? ''}
                  onChange={(e) =>
                    rhfField.onChange(
                      e.target.value === '' ? null : e.target.value
                    )
                  }
                  onBlur={rhfField.onBlur}
                  className="w-full"
                  placeholder={LABELS.NOTE}
                  id={rhfField.name}
                  onKeyDown={preventEnterSubmit}
                  disabled={!isEditing}
                />
              </Form.Item>
            )}
          />
        </td>
        {/* 削除ボタン (編集時のみ) */}
        {isEditing && (
          <td className="p-2 text-right">
            <Button
              onClick={handleRemove}
              danger
              data-testid={`${fieldArrayName}-${index}-remove`}
            >
              {ACTIONS.DELETE}
            </Button>
          </td>
        )}
      </Tr>
      {/* 展開行: 税区分・内税・切り上げ切り捨てを主行直下に縦展開 (横幅は増やさない) */}
      {isDetailOpen && (
        <Tr
          data-testid={`shipment-order-additional-${fieldArrayName}-${index}-detail`}
        >
          <td colSpan={columnCount} className="bg-gray-50 px-4 py-2">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 ps-12">
              {/* 税区分 (VOC_PJ-867 #3821) */}
              <div className="flex items-center gap-2">
                <span className="whitespace-nowrap text-secondary">
                  {LABELS.TAX_CATEGORY}
                </span>
                <Controller
                  control={control}
                  name={`${rowKeyPrefix}.taxCategoryId`}
                  render={({ field: rhfField, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="mb-0"
                    >
                      <TaxCategorySelect
                        tenantKey={tenantKey}
                        value={rhfField.value ?? undefined}
                        onChange={(v) => rhfField.onChange(v ?? null)}
                        onBlur={rhfField.onBlur}
                        placeholder={LABELS.TAX_CATEGORY}
                        disabled={!isEditing}
                        className="min-w-[160px]"
                      />
                    </Form.Item>
                  )}
                />
              </div>
              {/* 内税 (VOC_PJ-844 #3624): === true のみ内税、OFF/未設定は外税。 */}
              <Controller
                control={control}
                name={`${rowKeyPrefix}.isTaxInclusive`}
                render={({ field: rhfField }) => (
                  <Checkbox
                    checked={rhfField.value === true}
                    onChange={(e) => rhfField.onChange(e.target.checked)}
                    disabled={!isEditing}
                    data-testid={`${fieldArrayName}-${index}-tax-inclusive-checkbox`}
                  >
                    {LABELS.TAX_INCLUSIVE}
                  </Checkbox>
                )}
              />
              {/* 端数処理 (roundType) (VOC_PJ-888 #3917)。null=カスケード fallback
                  (拠点/荷主/マスタ既定)。手動で切上/切捨/四捨五入を上書き可能。
                  運行実績編集と同じ Select。 */}
              <div className="flex items-center gap-2">
                <span className="whitespace-nowrap text-secondary">
                  {LABELS.ROUNDING_UP_DOWN}
                </span>
                <Controller
                  control={control}
                  name={`${rowKeyPrefix}.roundType`}
                  render={({ field: rhfField, fieldState: { error } }) => (
                    <Form.Item
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="mb-0"
                    >
                      <Select
                        options={ROUND_TYPE_OPTIONS}
                        value={rhfField.value ?? undefined}
                        onChange={(v) => rhfField.onChange(v ?? null)}
                        onBlur={rhfField.onBlur}
                        placeholder={LABELS.ROUNDING_UP_DOWN}
                        disabled={!isEditing}
                        className="min-w-[160px]"
                      />
                    </Form.Item>
                  )}
                />
              </div>
            </div>
          </td>
        </Tr>
      )}
    </>
  )
}
